import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../hooks";
import { showSnackbar } from "../../reducers/snackbarSlice";
import { listCompanies, createCompany, updateCompany, Company } from "./CompaniesRepository";

const ColorSwatch: React.FC<{ color: string | null }> = ({ color }) => (
  <Box
    sx={{
      width: 20,
      height: 20,
      borderRadius: "4px",
      border: "1px solid rgba(0,0,0,0.2)",
      bgcolor: color || "transparent",
      display: "inline-block",
    }}
    title={color || ""}
  />
);

interface CreateForm {
  name: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  logo: string;
  loginImage: string;
  adminFullName: string;
  adminEmail: string;
}

const emptyCreateForm: CreateForm = {
  name: "",
  primaryColor: "",
  secondaryColor: "",
  tertiaryColor: "",
  logo: "",
  loginImage: "",
  adminFullName: "",
  adminEmail: "",
};

const CompaniesPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<Company | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [createForm, setCreateForm] = useState<CreateForm>(emptyCreateForm);

  const refresh = React.useCallback(() => {
    return listCompanies()
      .then(setCompanies)
      .catch((error) => {
        console.error(error);
        dispatch(showSnackbar({ message: t("companies.load_failed"), severity: "error" }));
      });
  }, [dispatch, t]);

  useEffect(() => {
    let active = true;
    refresh().finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [refresh]);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const result = await createCompany({
        name: createForm.name,
        primaryColor: createForm.primaryColor || undefined,
        secondaryColor: createForm.secondaryColor || undefined,
        tertiaryColor: createForm.tertiaryColor || undefined,
        logo: createForm.logo || undefined,
        loginImage: createForm.loginImage || undefined,
        adminFullName: createForm.adminFullName,
        adminEmail: createForm.adminEmail,
      });
      dispatch(showSnackbar({ message: t("companies.created", { email: result.adminEmail }), severity: "success" }));
      setCreateOpen(false);
      setCreateForm(emptyCreateForm);
      await refresh();
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: t("companies.create_failed"), severity: "error" }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    setSubmitting(true);
    try {
      await updateCompany(editTarget.id, {
        name: editTarget.name,
        primaryColor: editTarget.primaryColor || undefined,
        secondaryColor: editTarget.secondaryColor || undefined,
        tertiaryColor: editTarget.tertiaryColor || undefined,
        logo: editTarget.logo || undefined,
        loginImage: editTarget.loginImage || undefined,
      });
      dispatch(showSnackbar({ message: t("companies.updated"), severity: "success" }));
      setEditTarget(null);
      await refresh();
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: t("companies.update_failed"), severity: "error" }));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const createValid = createForm.name && createForm.adminFullName && createForm.adminEmail;

  return (
    <Box data-cy="companies-page">
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">{t("companies.title")}</Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)} data-cy="new-company">
          {t("companies.new_company")}
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table data-cy="companies-table">
          <TableHead>
            <TableRow>
              <TableCell>{t("companies.name")}</TableCell>
              <TableCell>{t("companies.colors")}</TableCell>
              <TableCell align="right">{t("generic.edit")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>{t("companies.no_companies")}</TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <TableRow key={company.id} data-cy="company-row">
                  <TableCell>{company.name}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <ColorSwatch color={company.primaryColor} />
                      <ColorSwatch color={company.secondaryColor} />
                      <ColorSwatch color={company.tertiaryColor} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setEditTarget(company)} data-cy="edit-company">
                      {t("generic.edit")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("companies.create_title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t("companies.name_label")}
              required
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              data-cy="company-name"
            />
            <Typography variant="caption" color="text.secondary">
              {t("companies.colors_optional_hint")}
            </Typography>
            <TextField
              label={t("companies.primary_color_label")}
              value={createForm.primaryColor}
              onChange={(e) => setCreateForm({ ...createForm, primaryColor: e.target.value })}
              data-cy="company-primary-color"
            />
            <TextField
              label={t("companies.secondary_color_label")}
              value={createForm.secondaryColor}
              onChange={(e) => setCreateForm({ ...createForm, secondaryColor: e.target.value })}
            />
            <TextField
              label={t("companies.tertiary_color_label")}
              value={createForm.tertiaryColor}
              onChange={(e) => setCreateForm({ ...createForm, tertiaryColor: e.target.value })}
            />
            <TextField
              label={t("companies.logo_label")}
              value={createForm.logo}
              onChange={(e) => setCreateForm({ ...createForm, logo: e.target.value })}
            />
            <TextField
              label={t("companies.login_image_label")}
              value={createForm.loginImage}
              onChange={(e) => setCreateForm({ ...createForm, loginImage: e.target.value })}
            />
            <Divider />
            <Typography variant="subtitle2">{t("companies.admin_section")}</Typography>
            <TextField
              label={t("companies.admin_name_label")}
              required
              value={createForm.adminFullName}
              onChange={(e) => setCreateForm({ ...createForm, adminFullName: e.target.value })}
              data-cy="company-admin-name"
            />
            <TextField
              label={t("companies.admin_email_label")}
              required
              type="email"
              value={createForm.adminEmail}
              onChange={(e) => setCreateForm({ ...createForm, adminEmail: e.target.value })}
              data-cy="company-admin-email"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t("generic.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!createValid || submitting}
            data-cy="submit-company"
          >
            {t("generic.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editTarget !== null} onClose={() => setEditTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("companies.edit_title")}</DialogTitle>
        <DialogContent>
          {editTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label={t("companies.name_label")}
                value={editTarget.name ?? ""}
                onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })}
                data-cy="edit-company-name"
              />
              <TextField
                label={t("companies.primary_color_label")}
                value={editTarget.primaryColor ?? ""}
                onChange={(e) => setEditTarget({ ...editTarget, primaryColor: e.target.value })}
              />
              <TextField
                label={t("companies.secondary_color_label")}
                value={editTarget.secondaryColor ?? ""}
                onChange={(e) => setEditTarget({ ...editTarget, secondaryColor: e.target.value })}
              />
              <TextField
                label={t("companies.tertiary_color_label")}
                value={editTarget.tertiaryColor ?? ""}
                onChange={(e) => setEditTarget({ ...editTarget, tertiaryColor: e.target.value })}
              />
              <TextField
                label={t("companies.logo_label")}
                value={editTarget.logo ?? ""}
                onChange={(e) => setEditTarget({ ...editTarget, logo: e.target.value })}
              />
              <TextField
                label={t("companies.login_image_label")}
                value={editTarget.loginImage ?? ""}
                onChange={(e) => setEditTarget({ ...editTarget, loginImage: e.target.value })}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>{t("generic.cancel")}</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={submitting} data-cy="submit-edit-company">
            {t("generic.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompaniesPage;
